// Sleep catch-up review modal (CATCHUP_WIDGETS_PLAN.md Task 7). Lets the user fill
// in a missing overnight sleep record for a gap night from the past week, prefilled
// with the patient's typical bed/wake times and quality (A3). Direct save, no
// pending/confirm, no Bedrock -- POST /catch-up/log. Bed time before 18:00 is
// treated as past midnight (belongs to the night's own calendar day); 18:00 or
// later belongs to the prior calendar day (D-CU-9).

import { useState } from "react";
import type { CatchUpLogEntry } from "@medassist/shared";
import { formatCalendarDate } from "@medassist/shared";
import { logCatchUpEntries, ApiError } from "../lib/api.js";
import { invalidateCatchUp } from "../lib/catchUpFetch.js";
import { ModalShell } from "./ModalShell.js";
import { MoonIcon } from "./icons.js";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayStr(): string {
  return localDateStr(new Date());
}

function daysAgoStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDateStr(d);
}

function prevDayStr(dayKey: string): string {
  const [y, m, d] = dayKey.split("-").map(Number);
  const dt = new Date(y!, m! - 1, d!);
  dt.setDate(dt.getDate() - 1);
  return localDateStr(dt);
}

function nightHeading(nightKey: string): string {
  if (nightKey === todayStr()) return "Last night";
  if (nightKey === daysAgoStr(1)) return "Yesterday night";
  return `${formatCalendarDate(nightKey, { weekday: "short", month: "short", day: "numeric" })} night`;
}

function dateTimeToIso(date: string, time: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const [h, m] = time.split(":").map(Number);
  return new Date(year!, month! - 1, day!, h ?? 0, m ?? 0, 0, 0).toISOString();
}

/** Bed time >= 18:00 belongs to the night before nightKey; earlier (past midnight) belongs to nightKey itself. */
function bedTimeIso(nightKey: string, bedTime: string): string {
  const h = Number(bedTime.split(":")[0]);
  const date = h >= 18 ? prevDayStr(nightKey) : nightKey;
  return dateTimeToIso(date, bedTime);
}

function wakeTimeIso(nightKey: string, wakeTime: string): string {
  return dateTimeToIso(nightKey, wakeTime);
}

const QUALITY_SCALES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

interface Staged {
  bedTime: string; // HH:MM local
  wakeTime: string; // HH:MM local
  quality: number;
}

/** Matches api.ts's catchUpEntryKey for sleep entries. */
function entryKeyFor(nightKey: string): string {
  return `sleep|${nightKey}|`;
}

export function SleepCatchUpModal({
  patientId,
  gapNights,
  typicalBedTime,
  typicalWakeTime,
  typicalQuality,
  onClose,
  onLogged,
}: {
  patientId: string;
  gapNights: string[];
  typicalBedTime: string;
  typicalWakeTime: string;
  typicalQuality: number;
  onClose: () => void;
  /** Called after at least one entry saves so the widget can refetch + toast. */
  onLogged: (count: number) => void;
}) {
  const [staged, setStaged] = useState<Map<string, Staged>>(new Map());
  const [settled, setSettled] = useState<Set<string>>(new Set());
  const [skipReasons, setSkipReasons] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visible = gapNights.filter((n) => !settled.has(n));

  function stage(nightKey: string) {
    setStaged((prev) =>
      new Map(prev).set(nightKey, {
        bedTime: typicalBedTime,
        wakeTime: typicalWakeTime,
        quality: Math.round(typicalQuality),
      })
    );
  }

  function unstage(nightKey: string) {
    setStaged((prev) => {
      const next = new Map(prev);
      next.delete(nightKey);
      return next;
    });
  }

  function update(nightKey: string, patch: Partial<Staged>) {
    setStaged((prev) => {
      const existing = prev.get(nightKey);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(nightKey, { ...existing, ...patch });
      return next;
    });
  }

  async function save() {
    if (staged.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const entries: CatchUpLogEntry[] = [...staged.entries()].map(([nightKey, s]) => ({
        kind: "sleep",
        nightKey,
        startedAt: bedTimeIso(nightKey, s.bedTime),
        endedAt: wakeTimeIso(nightKey, s.wakeTime),
        qualityRating: s.quality,
      }));
      const { saved, skipped } = await logCatchUpEntries(patientId, entries);
      const nowSettled = new Set(settled);
      const reasons = new Map(skipReasons);
      const stagedNights = [...staged.keys()];
      for (const s of saved) {
        const nightKey = stagedNights.find((n) => entryKeyFor(n) === s.entryKey);
        if (nightKey) nowSettled.add(nightKey);
      }
      for (const s of skipped) {
        const nightKey = stagedNights.find((n) => entryKeyFor(n) === s.entryKey);
        if (nightKey) {
          nowSettled.add(nightKey);
          reasons.set(nightKey, s.reason);
        }
      }
      setSettled(nowSettled);
      setSkipReasons(reasons);
      setStaged(new Map());
      invalidateCatchUp(patientId);
      if (saved.length > 0) onLogged(saved.length);
      if (skipped.length === 0) {
        onClose();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save");
    } finally {
      setBusy(false);
    }
  }

  const nothingLeft = visible.length === 0 && skipReasons.size === 0;

  function renderNight(nightKey: string) {
    const s = staged.get(nightKey);

    return (
      <li key={nightKey} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="flex-1 font-medium text-gray-900 dark:text-gray-100">{nightHeading(nightKey)}</span>
          {!s && (
            <button
              onClick={() => stage(nightKey)}
              className="shrink-0 rounded-full bg-(--accent) px-3 py-1 text-xs font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
            >
              Log it
            </button>
          )}
        </div>

        {s && (
          <div className="mt-2 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">
                Bed
                <input
                  type="time"
                  value={s.bedTime}
                  onChange={(e) => update(nightKey, { bedTime: e.target.value })}
                  className="ml-1.5 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
                />
              </label>
              <label className="text-xs text-gray-500 dark:text-gray-400">
                Wake
                <input
                  type="time"
                  value={s.wakeTime}
                  onChange={(e) => update(nightKey, { wakeTime: e.target.value })}
                  className="ml-1.5 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
                />
              </label>
              <button
                onClick={() => unstage(nightKey)}
                className="ml-auto rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                Undo
              </button>
            </div>
            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">Quality {s.quality}/10</div>
              <div className="grid grid-cols-10 gap-1">
                {QUALITY_SCALES.map((q) => {
                  const selected = s.quality === q;
                  return (
                    <button
                      key={q}
                      onClick={() => update(nightKey, { quality: q })}
                      className={`rounded-md border py-1 text-[10px] ${
                        selected
                          ? "border-(--accent) bg-(--accent)/10 font-semibold text-(--accent)"
                          : "border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`}
                    >
                      {q}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </li>
    );
  }

  return (
    <ModalShell
      title="Catch up on sleep"
      icon={<MoonIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
      footer={
        !nothingLeft && (
          <button
            onClick={() => void save()}
            disabled={busy || staged.size === 0}
            className="w-full rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
          >
            {busy ? "Saving…" : `Save ${staged.size} ${staged.size === 1 ? "night" : "nights"}`}
          </button>
        )
      }
    >
      {error && (
        <p className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </p>
      )}

      {[...skipReasons.entries()].map(([nightKey, reason]) => (
        <p
          key={nightKey}
          className="mb-2 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
        >
          {nightHeading(nightKey)}: {reason}
        </p>
      ))}

      {nothingLeft ? (
        <p className="py-6 text-center text-sm text-gray-600 dark:text-gray-300">All caught up ✓</p>
      ) : (
        <ul className="space-y-2">{visible.map((n) => renderNight(n))}</ul>
      )}
    </ModalShell>
  );
}
